import {
  memo,
  ReactElement,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { checkIsElementScrollable } from 'utils/generic/checkIsElementScrollable';
import { isMobile } from 'utils/is-mobile';
import { Region, Sport } from 'interfaces/sportsbook-data-levels';
import { RegionLevelNavigation } from './RegionLevelNavigation';
import { SportLevelNavigation } from './SportLevelNavigation';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useGameScrollIntoView } from 'hooks/useGameScrollIntoView';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import {
  getNavigationWrapperHeightChanged,
  getUserSportOrder
} from 'store/selectors/new-sportsbook';
import { getScrollToCompetitionId } from 'store/selectors/sport-data';
import './index.less';

interface NavigationCollapse {
  levelAlias: string;
}

interface NavigationCollapseRegionLevel extends NavigationCollapse {
  regionLevel: boolean;
  navigationData: Region[];
  sportLevel?: boolean;
}

interface NavigationCollapseSportLevel extends NavigationCollapse {
  sportLevel: boolean;
  navigationData: Sport[];
  regionLevel?: boolean;
}

// This component requires one of regionLevel | sportLevel props
type TNavigationCollapseWrapperProps =
  | NavigationCollapseRegionLevel
  | NavigationCollapseSportLevel;

const INITIAL_COUNT = 15;
const LOAD_MORE_COUNT = 15;
const THRESHOLD_COUNT = 5;

export const NavigationCollapseWrapper = memo(
  ({
    navigationData,
    levelAlias,
    regionLevel,
    sportLevel
  }: TNavigationCollapseWrapperProps): ReactElement => {
    const { params } = useSbRouteMatch();
    const configs = useSportsbookConfigs();

    const trigger = useSelector(getNavigationWrapperHeightChanged);
    const scrollToCompetitionId = useSelector(getScrollToCompetitionId);
    const userSportOrder = useSelector(getUserSportOrder);
    const prevSelectedRegionIndex = useMemo(() => {
      if (regionLevel) {
        return navigationData.findIndex(
          region =>
            (scrollToCompetitionId?.id &&
              region?.competition?.[scrollToCompetitionId.id]) ||
            params.region === region.alias
        );
      }

      if (sportLevel) {
        return (navigationData as Sport[]).findIndex(sport =>
          Object.values(sport.region || {}).find(
            region =>
              scrollToCompetitionId?.id &&
              !!region.competition?.[scrollToCompetitionId.id]
          )
        );
      }

      return -1;
    }, [
      regionLevel,
      sportLevel,
      navigationData,
      scrollToCompetitionId,
      params
    ]);

    const initialCount =
      SpringConfigs.RENDER_COMPETITIONS_IN_DOM || configs.fixedScroll
        ? navigationData.length
        : prevSelectedRegionIndex < INITIAL_COUNT
        ? INITIAL_COUNT
        : prevSelectedRegionIndex + THRESHOLD_COUNT;

    const [itemsSliceCount, setItemsSliceCount] = useState(initialCount);

    useLayoutEffect(() => {
      if (
        !checkIsElementScrollable('#prematch-left-sidebar-wrapper') &&
        !isMobile() &&
        !configs.fixedScroll
      ) {
        setItemsSliceCount(navigationData.length);
      }
    }, [trigger]);

    const orderedSportData = useMemo(() => {
      return navigationData && sportLevel
        ? navigationData?.slice(0, itemsSliceCount)
        : navigationData;
    }, [userSportOrder, navigationData, itemsSliceCount]);

    const scrollToRef = useRef(null);

    const handleScroll = useGameScrollIntoView();

    const isMoreDataAvailable = itemsSliceCount < navigationData.length;

    const loadMore = () => {
      setItemsSliceCount(prevCount => prevCount + LOAD_MORE_COUNT);
    };

    useLayoutEffect(() => {
      if (
        prevSelectedRegionIndex &&
        prevSelectedRegionIndex !== -1 &&
        scrollToRef.current
      ) {
        handleScroll(
          scrollToRef.current,
          'competition',
          scrollToCompetitionId?.top,
          true
        );
      }
    }, [prevSelectedRegionIndex]);

    const generateContent = () => {
      if (regionLevel) {
        return (
          <RegionLevelNavigation
            navItemsToRender={navigationData as Region[]}
            itemsSliceCount={itemsSliceCount}
            sportAlias={levelAlias}
            ref={scrollToRef}
          />
        );
      }

      if (sportLevel) {
        return (
          <SportLevelNavigation
            navItemsToRender={orderedSportData as Sport[]}
            category={levelAlias}
            ref={scrollToRef}
          />
        );
      }
    };

    return (
      <InfiniteScroll
        scrollableTarget={isMobile() ? '' : 'prematch-left-sidebar-wrapper'}
        dataLength={itemsSliceCount}
        next={loadMore}
        hasMore={isMoreDataAvailable}
        scrollThreshold={0.6}
        loader={null}
      >
        {generateContent()}
      </InfiniteScroll>
    );
  }
);
